import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { LangConstant } from "const";
import { CommonUtils } from "utils";

const ActionDialog = ({
  open,
  position,
  units,
  departments,
  selectedUnitId,
  selectedDepartmentId,
  onClose,
  onCreate,
  onUpdate,
  onChangeUnit,
}) => {
  const isEdit = Boolean(position);
  const { t: getLabel } = useTranslation();
  const inputRef = useRef(null);

  const [form, setForm] = useState({
    positionName: position?.positionName || "",
    unitId: position?.unitId || "",
    departmentId: position?.departmentId || "",
    positionId: position?.positionId || "",
  });

  const handleChangeForm = event => {
    if (event.target.name === "positionName") {
      setForm(currentState => ({ ...currentState, [event.target.name]: CommonUtils.capitalizeFirstLetter(event.target.value) }));
    } else {
      setForm(currentState => ({ ...currentState, [event.target.name]: event.target.value }));
    }
    if (event.target.name === "unitId") {
      onChangeUnit(event.target.value);
      setForm(currentState => ({ ...currentState, departmentId: "" }));
    }
  };

  const handleSubmit = () => {
    isEdit ? onUpdate(form) : onCreate(form);
  };

  useEffect(() => {
    if (open && position) {
      onChangeUnit(position?.unitId);
      setForm({
        positionName: position?.positionName || "",
        unitId: position?.unitId || "",
        departmentId: position?.departmentId || "",
        positionId: position?.positionId || "",
      });
    } else if (open) {
      const initUnitId = selectedUnitId && String(selectedUnitId) !== "0" && String(selectedUnitId) !== "-1" ? String(selectedUnitId) : "";
      const initDeptId = selectedDepartmentId && String(selectedDepartmentId) !== "0" ? String(selectedDepartmentId) : "";

      if (initUnitId) {
        onChangeUnit(initUnitId);
      }
      setForm({
        positionName: "",
        unitId: initUnitId,
        departmentId: initDeptId,
        positionId: "",
      });
    }
  }, [open, position, selectedUnitId, selectedDepartmentId]);

  // Auto focus input when opened
  useEffect(() => {
    if (open) {
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 100);
    }
  }, [open]);

  // ESC key handler
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = e => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <>
      <div className="modal-backdrop fade show" />
      <div className="modal modal-blur fade show" style={{ display: "block" }} tabIndex="-1">
        <div className="modal-dialog modal-sm modal-dialog-centered" style={{ maxWidth: 600 }}>
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" style={{ fontWeight: "bold" }}>
                {getLabel(isEdit ? "TXT_EDIT_POSITION_TITLE" : "TXT_CREATE_POSITION_TITLE")}
              </h5>
              <button type="button" className="btn-close" aria-label="Close" onClick={onClose}></button>
            </div>
            <div className="modal-body">
              <div className="d-flex flex-column gap-3" style={{ padding: "8px 0" }}>
                <div>
                  <label className="form-label">{getLabel("TXT_POSITION_NAME")}</label>
                  <input
                    ref={inputRef}
                    autoFocus
                    type="text"
                    className="form-control"
                    placeholder={getLabel("P_POSITION_NAME")}
                    value={form.positionName}
                    name="positionName"
                    onChange={handleChangeForm}
                  />
                </div>
                <div>
                  <label className="form-label">{getLabel("TXT_FROM_UNIT")}</label>
                  <select
                    className="form-select"
                    value={form.unitId}
                    name="unitId"
                    onChange={handleChangeForm}
                  >
                    <option value="">{getLabel("TXT_CHOOSE_UNIT")}</option>
                    {units.map(unit => (
                      <option value={unit.unitId} key={unit.unitId}>
                        {unit.unitName}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="form-label">{getLabel("TXT_FROM_DEPARTMENT")}</label>
                  <select
                    className="form-select"
                    value={form.departmentId}
                    name="departmentId"
                    onChange={handleChangeForm}
                    disabled={!Boolean(form.unitId)}
                  >
                    <option value="">{getLabel("TXT_CHOOSE_DEPARTMENT")}</option>
                    {departments.map(department => (
                      <option value={department.departmentId} key={department.departmentId}>
                        {department.departmentName}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="d-flex align-items-center justify-content-center gap-2">
                  <button className="btn btn-outline-danger w-100" onClick={onClose}>
                    {getLabel(LangConstant.L_CANCEL_BUTTON)}
                  </button>
                  <button
                    className="btn btn-primary w-100"
                    disabled={!Boolean(form.unitId && form.positionName)}
                    onClick={handleSubmit}
                  >
                    {getLabel(isEdit ? LangConstant.L_UPDATE : LangConstant.L_CREATE)}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ActionDialog;
